/**
 * A compact secondary row INSIDE a card or a detail — a track, a filing, a
 * nearby place, an edition.
 *
 * It is not `CresultcardRow`. That one is a member of a bounded set the reader
 * scans across; this one is a line item inside a single result, hairline
 * separated within a tile, and it never carries an open hint because there is
 * nothing below it to open.
 */
"use client";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { Cbutton } from "@/components/ui/Cbutton";
import { ButtonPreset } from "@/constants/componentPresets";
import { useCresultcardNestedRadius } from "./cresultcard.radius";
import { useCrcTouchExpansion } from "./cresultcard.touch";
import {
  CRC_DIVIDER,
  CRC_NUMERIC_STYLE,
  CRC_TEXT,
  CRC_TEXT_FAINT,
  CRC_TRUNCATE_STYLE,
  crcTextClampStyle,
} from "./cresultcard.tokens";
import { CresultcardTitleLines } from "./cresultcard.types";
import { useCresultcardViewport } from "./cresultcard.viewport";

const ROW_GAP = 9;
const ROW_BLOCK_PAD = 7;
const ORDINAL_WIDTH = 16;
const LINK_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/ListRow/Link",
  "data-crc-control": true,
};
/**
 * The link's surface starts on the leading edge, and this is the only property
 * that can put it there: it reaches the `Ccontainer` as a PROP, where `Cbutton`
 * defaults it to the centring an icon-and-label control wants, and a
 * `textAlign` or a `justifyContent` in the caller's `style` cannot outrank a
 * prop. This call site predates that contract and stated its intent in `style`
 * alone, which is the trap `CresultcardIdentity` already carries the fix for.
 *
 * The block axis keeps Cbutton's centring on purpose. A list row's title is one
 * or two lines with nothing above or below it inside the control, so there is
 * nothing for a `flex-start` to align to that centring does not already get
 * right — and the row's grid centres its three zones against each other anyway.
 */
const LINK_JUSTIFY_CONTENT = "flex-start";
const LINK_SURFACE_STYLE: CSSProperties = {
  minWidth: 0,
  textAlign: "start",
};

interface CresultcardListRowBaseProps {
  /** A position in the list, when position is part of the record. */
  readonly ordinal?: number;
  readonly mark?: ReactNode;
  /**
   * How many lines the title may take. One line, cut, unless declared — the
   * row used to truncate unconditionally, so a filing whose name IS the record
   * had no way to ask for a second line and lost the half of itself that
   * distinguished it from the filing above.
   */
  readonly titleLines?: CresultcardTitleLines;
  /**
   * `ReactNode`, not `string`: a subtitle carrying a formatted figure, a tone
   * or a separated pair is the ordinary case, and a `string` type forced every
   * such caller to flatten it to text before the row could render it.
   */
  readonly subtitle?: ReactNode;
  /** The row's own figure — a duration, a count, a price. */
  readonly value?: ReactNode;
  readonly action?: ReactNode;
  readonly onLast?: boolean;
}

type CresultcardListRowProps = CresultcardListRowBaseProps &
  (
    | { readonly href: string; readonly title: string }
    | { readonly href?: undefined; readonly title: ReactNode }
  );

export function CresultcardListRow({
  ordinal,
  mark,
  title,
  titleLines = CresultcardTitleLines.One,
  subtitle,
  value,
  action,
  href,
  onLast = false,
}: CresultcardListRowProps): ReactElement {
  const radius = useCresultcardNestedRadius();
  const { isMobile } = useCresultcardViewport();
  /**
   * SPEC §7.4's minimum target, bounded by the one thing that bounds it here.
   *
   * The rows are hairline-separated siblings and each linked row's title is its
   * own control, so this is the single call site in the kit where an unbounded
   * expansion would put one row's target over the row above it. The ceiling is
   * the row's own block padding: the hit area grows to fill the space the row
   * already owns and stops at the divider. It reaches 27.8px of the 44px floor
   * from a 13.8px link — and the smallest adjacent gap between two linked rows
   * is 7.99px, so the ceiling is what makes even that reach honest. Closing the
   * rest means giving the ROW a Mobile height, which is a layout decision and
   * not a control's to make.
   */
  const linkTouch = useCrcTouchExpansion({
    isMobile,
    maxExpansion: ROW_BLOCK_PAD,
  });
  const linkRootStyle: CSSProperties = {
    ...LINK_SURFACE_STYLE,
    ...linkTouch.style,
  };

  const titleNode = (
    <span
      style={{
        ...crcTextClampStyle({ lines: titleLines }),
        minWidth: 0,
        fontSize: 11.5,
        fontWeight: 640,
        lineHeight: 1.2,
        color: CRC_TEXT,
      }}
    >
      {title}
    </span>
  );

  return (
    <div
      data-el="Cresultcard/ListRow/Root"
      style={{
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 1fr) auto",
        alignItems: "center",
        gap: ROW_GAP,
        minWidth: 0,
        paddingBlock: ROW_BLOCK_PAD,
        borderBottom: onLast ? undefined : `1px solid ${CRC_DIVIDER}`,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          flex: "0 0 auto",
        }}
      >
        {ordinal != null && (
          <span
            style={{
              ...CRC_NUMERIC_STYLE,
              width: ORDINAL_WIDTH,
              fontSize: 10,
              fontWeight: 700,
              textAlign: "end",
              color: CRC_TEXT_FAINT,
            }}
          >
            {ordinal}
          </span>
        )}
        {mark}
      </span>

      <span
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minWidth: 0,
        }}
      >
        {href == null ? (
          titleNode
        ) : (
          <Cbutton
            anchorRootRef={linkTouch.measureRoot}
            ariaLabel={title}
            containerStyle={LINK_SURFACE_STYLE}
            contentStyle={LINK_SURFACE_STYLE}
            disableClickMotion
            disableHoverEffects
            href={href}
            justifyContent={LINK_JUSTIFY_CONTENT}
            padding={0}
            preset={ButtonPreset.Ghost}
            radius={radius}
            rootAttributes={{
              ...LINK_ROOT_ATTRIBUTES,
            }}
            style={linkRootStyle}
          >
            {titleNode}
          </Cbutton>
        )}
        {subtitle != null && (
          <span
            style={{
              ...CRC_TRUNCATE_STYLE,
              display: "block",
              fontSize: 9.5,
              fontWeight: 540,
              color: CRC_TEXT_FAINT,
            }}
          >
            {subtitle}
          </span>
        )}
      </span>

      <span
        style={{
          ...CRC_NUMERIC_STYLE,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          flex: "0 0 auto",
        }}
      >
        {value}
        {action}
      </span>
    </div>
  );
}
